// SDE.3 — Ondas 2.1.b + 2.1.c. Admin do Business Kit — CMS editorial.
// Onda 2.1.c: consome contrato explícito de getDeliverableAdminDetail,
// exibe métricas agregadas server-side, contadores de eventos não classificados,
// "Dados confiáveis desde" e "Atualizado em".

import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  AlertTriangle, CheckCircle2, XCircle, Circle, FileText, Play,
  Pause, Ban, Eye, ExternalLink, RefreshCw, Filter, TrendingUp,
  MessageSquare, GitBranch, ListChecks, Users, History, Clock, ShieldAlert,
} from "lucide-react";
import { PageHeader, Toolbar, DataCard, EmptyState, StatusPill } from "@/components/admin/AdminPrimitives";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import {
  listPublicationOverview, updatePublication, getDeliverableAdminDetail,
  setChecklistCriterion, updateRolloutGroup,
} from "@/lib/sde/publication.functions";
import {
  PUBLICATION_STATUS_META, ROLLOUT_GROUP_META,
  type PublicationStatus, type RolloutGroupKey,
} from "@/lib/sde/publication";
import { DELIVERABLE_REGISTRY, REGISTRY_BY_KEY } from "@/lib/sde/registry";
import { CATEGORY_META } from "@/lib/sde/types";
import { useCriticalMutation } from "@/hooks/useCriticalMutation";
import { ReasonDialog } from "@/components/admin/sde/ReasonDialog";
import { ConflictDialog } from "@/components/admin/sde/ConflictDialog";
import { AccessibleRolloutSlider, type SliderPersistState } from "@/components/admin/sde/AccessibleRolloutSlider";
import { formatRate, formatSeconds, APP_TIMEZONE } from "@/lib/sde/metrics";
import { HOMOLOGATION_CRITERIA } from "@/lib/sde/publication";

export const Route = createFileRoute("/_authenticated/admin/entregaveis")({
  head: () => ({ meta: [{ title: "Business Kit — Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: BusinessKitAdmin,
});

const STATUS_TONE: Record<PublicationStatus, "green" | "gold" | "gray" | "red" | "navy"> = {
  draft: "gray", internal_review: "navy", homologation: "gold",
  published: "green", paused: "red", deprecated: "red",
};

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR", { timeZone: APP_TIMEZONE }); }
  catch { return "—"; }
}
function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-BR", { timeZone: APP_TIMEZONE }); }
  catch { return "—"; }
}

function BusinessKitAdmin() {
  const overviewFn = useServerFn(listPublicationOverview);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin", "sde", "overview"],
    queryFn: () => overviewFn({}),
  });

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [drawerKey, setDrawerKey] = useState<string | null>(null);

  const rows = useMemo(() => {
    if (!data) return [];
    const pubByKey = new Map(data.publications.map((p) => [p.key, p]));
    const entries = DELIVERABLE_REGISTRY.map((entry) => {
      const pub = pubByKey.get(entry.key);
      const status = (pub?.publication_status ?? "draft") as PublicationStatus;
      const checklist = data.checklistByKey[entry.key];
      const readiness = checklist
        ? Math.round((checklist.requiredApproved / Math.max(1, data.requiredCanonical)) * 100)
        : 0;
      return { entry, pub, status, checklist, readiness, alerts: data.alertsByKey[entry.key] ?? 0 };
    });
    return entries.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (categoryFilter !== "all" && r.entry.category !== categoryFilter) return false;
      if (priorityFilter === "priority" && !r.entry.priority) return false;
      if (priorityFilter === "complementary" && r.entry.priority) return false;
      if (search && !r.entry.title.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [data, statusFilter, categoryFilter, priorityFilter, search]);

  const counts = useMemo(() => {
    const c: Record<PublicationStatus, number> = { published: 0, homologation: 0, internal_review: 0, paused: 0, deprecated: 0, draft: 0 };
    let alerts = 0;
    for (const p of data?.publications ?? []) c[p.publication_status as PublicationStatus] = (c[p.publication_status as PublicationStatus] || 0) + 1;
    for (const k in data?.alertsByKey ?? {}) alerts += data!.alertsByKey[k];
    return { ...c, alerts };
  }, [data]);

  return (
    <div className="p-6 lg:p-8">
      <PageHeader
        title="Business Kit"
        description="CMS editorial. Homologa, publica, pausa e monitora — sem tocar no conteúdo dos documentos."
        actions={
          <button onClick={() => refetch()} className="inline-flex h-9 items-center gap-2 rounded-lg border border-black/[0.08] bg-white px-3 text-sm font-semibold text-[var(--navy)] hover:bg-black/[0.02]">
            <RefreshCw size={14} /> Atualizar
          </button>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
        <SummaryCard label="Publicados" value={counts.published} tone="green" />
        <SummaryCard label="Homologação" value={counts.homologation} tone="gold" />
        <SummaryCard label="Revisão" value={counts.internal_review} tone="navy" />
        <SummaryCard label="Rascunho" value={counts.draft} tone="gray" />
        <SummaryCard label="Pausados" value={counts.paused} tone="red" />
        <SummaryCard label="Descontinuados" value={counts.deprecated} tone="red" />
        <SummaryCard label="Alertas abertos" value={counts.alerts} tone="red" icon={AlertTriangle} />
      </div>

      <Toolbar search={search} onSearch={setSearch} placeholder="Buscar entregável…">
        <FilterSelect value={statusFilter} onChange={setStatusFilter} label="Estado" options={[
          { value: "all", label: "Todos estados" },
          ...Object.entries(PUBLICATION_STATUS_META).map(([k, v]) => ({ value: k, label: v.label })),
        ]} />
        <FilterSelect value={categoryFilter} onChange={setCategoryFilter} label="Categoria" options={[
          { value: "all", label: "Todas categorias" },
          ...Object.entries(CATEGORY_META).map(([k, v]) => ({ value: k, label: v.title })),
        ]} />
        <FilterSelect value={priorityFilter} onChange={setPriorityFilter} label="Prioridade" options={[
          { value: "all", label: "Todas prioridades" },
          { value: "priority", label: "Prioritários" },
          { value: "complementary", label: "Complementares" },
        ]} />
      </Toolbar>

      <DataCard>
        {isLoading ? (
          <div className="p-10 text-center text-sm text-[var(--gray-ink)]">Carregando catálogo…</div>
        ) : rows.length === 0 ? (
          <EmptyState icon={Filter} title="Nenhum entregável encontrado" description="Ajuste os filtros para ver o catálogo." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-black/[0.06] bg-black/[0.02] text-xs uppercase tracking-wide text-[var(--gray-ink)]">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Nome</th>
                  <th className="px-4 py-3 text-left font-semibold">Categoria</th>
                  <th className="px-4 py-3 text-left font-semibold">Prioridade</th>
                  <th className="px-4 py-3 text-left font-semibold">Estado</th>
                  <th className="px-4 py-3 text-left font-semibold">Schema</th>
                  <th className="px-4 py-3 text-left font-semibold">Homologação</th>
                  <th className="px-4 py-3 text-left font-semibold">Alertas</th>
                  <th className="px-4 py-3 text-left font-semibold">Atualização</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/[0.04]">
                {rows.map((r) => (
                  <tr key={r.entry.key} className="cursor-pointer hover:bg-black/[0.015]" onClick={() => setDrawerKey(r.entry.key)}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[var(--navy)]">{r.entry.title}</div>
                      <div className="text-xs text-[var(--gray-ink)] line-clamp-1">{r.entry.description}</div>
                    </td>
                    <td className="px-4 py-3 text-[var(--gray-ink)]">{CATEGORY_META[r.entry.category].title}</td>
                    <td className="px-4 py-3">
                      {r.entry.priority ? <StatusPill tone="gold">MVP</StatusPill> : <StatusPill tone="gray">Extra</StatusPill>}
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill tone={STATUS_TONE[r.status]}>{PUBLICATION_STATUS_META[r.status].label}</StatusPill>
                    </td>
                    <td className="px-4 py-3 text-[var(--gray-ink)]">v{r.pub?.schema_version ?? r.entry.schemaVersion ?? 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Progress value={r.readiness} className="h-1.5 w-24" />
                        <span className="text-xs text-[var(--gray-ink)]">{r.readiness}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {r.alerts > 0 ? <StatusPill tone="red">{r.alerts}</StatusPill> : <span className="text-xs text-[var(--gray-ink)]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--gray-ink)]">
                      {formatDate(r.pub?.updated_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DataCard>

      {drawerKey && (
        <DeliverableDrawer
          key={drawerKey}
          deliverableKey={drawerKey}
          open={!!drawerKey}
          onClose={() => setDrawerKey(null)}
        />
      )}
    </div>
  );
}

function SummaryCard({ label, value, tone, icon: Icon }: { label: string; value: number; tone: "green" | "gold" | "gray" | "red" | "navy"; icon?: any }) {
  return (
    <div className="rounded-2xl border border-black/[0.06] bg-white p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">{label}</span>
        {Icon && <Icon size={14} className="text-[var(--gray-ink)]" />}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="font-display text-2xl font-extrabold text-[var(--navy)]">{value}</span>
        <StatusPill tone={tone}>&nbsp;</StatusPill>
      </div>
    </div>
  );
}

function FilterSelect({ value, onChange, label, options }: { value: string; onChange: (v: string) => void; label: string; options: { value: string; label: string }[] }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-[180px] bg-white text-sm"><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

// ============ DRAWER ============
function DeliverableDrawer({ deliverableKey, open, onClose }: { deliverableKey: string; open: boolean; onClose: () => void }) {
  const entry = REGISTRY_BY_KEY[deliverableKey];
  const detailFn = useServerFn(getDeliverableAdminDetail);
  const qc = useQueryClient();

  const { data, isLoading, isFetching, refetch, dataUpdatedAt } = useQuery({
    queryKey: ["admin", "sde", "detail", deliverableKey],
    queryFn: () => detailFn({ data: { key: deliverableKey } }),
    enabled: open,
    staleTime: 30_000, // métricas históricas: cache curto; overview invalida ao mutar
  });

  const [conflictDetected, setConflictDetected] = useState(false);

  const reloadAll = async () => {
    setConflictDetected(false);
    await Promise.all([
      refetch(),
      qc.invalidateQueries({ queryKey: ["admin", "sde", "overview"] }),
    ]);
  };

  if (!entry) return null;

  const publication = data?.publication ?? null;
  const pubUpdatedAt: string | null = publication?.updatedAt ?? null;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-[var(--gray-ink)]">
            <span>{CATEGORY_META[entry.category].title}</span>
            {entry.priority && <StatusPill tone="gold">MVP</StatusPill>}
          </div>
          <SheetTitle className="text-2xl">{entry.title}</SheetTitle>
          <SheetDescription>{entry.description}</SheetDescription>
        </SheetHeader>

        <FreshnessBar
          updatedAt={pubUpdatedAt}
          loadedAt={dataUpdatedAt}
          isFetching={isFetching}
          conflict={conflictDetected}
          onReload={reloadAll}
        />

        {data?.contractIssues && data.contractIssues.length > 0 && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            <ShieldAlert size={14} className="mt-0.5" />
            <div>
              <div className="font-semibold">Contrato do detalhe com {data.contractIssues.length} inconsistência(s).</div>
              <div className="text-[11px]">Métricas afetadas podem ficar indisponíveis. Chaves: {data.contractIssues.slice(0, 4).join(", ")}{data.contractIssues.length > 4 ? "…" : ""}</div>
            </div>
          </div>
        )}

        {publication && (
          <ActionBar
            currentStatus={publication.publicationStatus as PublicationStatus}
            deliverableKey={deliverableKey}
            expectedUpdatedAt={pubUpdatedAt}
            entityLabel={entry.title}
            onConflictDetected={() => setConflictDetected(true)}
            onDone={reloadAll}
          />
        )}

        <Tabs defaultValue="summary" className="mt-6">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="summary">Resumo</TabsTrigger>
            <TabsTrigger value="checklist">Checklist</TabsTrigger>
            <TabsTrigger value="deps">Depend.</TabsTrigger>
            <TabsTrigger value="versions">Versões</TabsTrigger>
            <TabsTrigger value="metrics">Métricas</TabsTrigger>
            <TabsTrigger value="feedback">Feedback</TabsTrigger>
            <TabsTrigger value="rollout">Rollout</TabsTrigger>
          </TabsList>

          {isLoading || !data ? (
            <div className="py-10 text-center text-sm text-[var(--gray-ink)]">Carregando…</div>
          ) : (
            <>
              <TabsContent value="summary"><SummaryTab publication={publication} entry={entry} totalUsers={data.totalUsersWithDeliverable} /></TabsContent>
              <TabsContent value="checklist">
                <ChecklistTab
                  checklist={data.checklist}
                  deliverableKey={deliverableKey}
                  entityLabel={entry.title}
                  onDone={reloadAll}
                  onConflictDetected={() => setConflictDetected(true)}
                />
              </TabsContent>
              <TabsContent value="deps"><DependenciesTab entry={entry} /></TabsContent>
              <TabsContent value="versions"><VersionsTab versions={data.versions} /></TabsContent>
              <TabsContent value="metrics">
                <MetricsTab
                  metrics={data.metrics}
                  unclassified={data.unclassifiedEvents}
                  alerts={data.alerts.length}
                  totalUsersWithDeliverable={data.totalUsersWithDeliverable}
                />
              </TabsContent>
              <TabsContent value="feedback"><FeedbackTab feedback={data.feedback} usefulRate={data.metrics?.usefulFeedbackRate ?? null} /></TabsContent>
              <TabsContent value="rollout">
                <RolloutTab
                  publication={publication}
                  rolloutGroups={data.rolloutGroups}
                  entityLabel={entry.title}
                  onDone={reloadAll}
                  onConflictDetected={() => setConflictDetected(true)}
                />
              </TabsContent>
            </>
          )}
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

// ============ FRESHNESS BAR ============
function FreshnessBar({ updatedAt, loadedAt, isFetching, conflict, onReload }: {
  updatedAt: string | null;
  loadedAt: number;
  isFetching: boolean;
  conflict: boolean;
  onReload: () => void;
}) {
  const loadedLabel = loadedAt ? new Date(loadedAt).toLocaleTimeString("pt-BR", { timeZone: APP_TIMEZONE }) : "—";
  return (
    <div className={`mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs ${conflict ? "border-amber-300 bg-amber-50" : "border-black/[0.06] bg-black/[0.02]"}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-1 text-[var(--gray-ink)]">
          <Clock size={12} /> Última atualização do registro: <strong className="text-[var(--navy)]">{formatDateTime(updatedAt)}</strong>
        </span>
        <span className="text-[var(--gray-ink)]">· Atualizado em {loadedLabel}</span>
        {conflict && (
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-white px-2 py-0.5 font-semibold text-amber-700">
            <AlertTriangle size={11} /> Alterado por outro administrador
          </span>
        )}
      </div>
      <button
        onClick={onReload}
        disabled={isFetching}
        className="inline-flex items-center gap-1.5 rounded-md border border-black/[0.08] bg-white px-2 py-1 font-semibold text-[var(--navy)] hover:bg-black/[0.03] disabled:opacity-50"
      >
        <RefreshCw size={11} className={isFetching ? "animate-spin" : ""} /> Recarregar estado
      </button>
    </div>
  );
}

// ============ ACTIONS ============
type Transition = { label: string; status: PublicationStatus; icon: any; tone: string; requiresReason: boolean };

const TRANSITIONS: Transition[] = [
  { label: "Revisão interna", status: "internal_review", icon: Eye, tone: "", requiresReason: false },
  { label: "Homologação", status: "homologation", icon: ListChecks, tone: "", requiresReason: false },
  { label: "Publicar", status: "published", icon: Play, tone: "text-[var(--green)]", requiresReason: false },
  { label: "Pausar", status: "paused", icon: Pause, tone: "text-red-600", requiresReason: true },
  { label: "Descontinuar", status: "deprecated", icon: Ban, tone: "text-red-600", requiresReason: true },
];

function ActionBar({
  currentStatus, deliverableKey, expectedUpdatedAt, entityLabel, onDone, onConflictDetected,
}: {
  currentStatus: PublicationStatus;
  deliverableKey: string;
  expectedUpdatedAt: string | null;
  entityLabel: string;
  onDone: () => void | Promise<void>;
  onConflictDetected: () => void;
}) {
  const updateFn = useServerFn(updatePublication);
  const [reasonPrompt, setReasonPrompt] = useState<{ transition: Transition } | null>(null);

  const mutation = useCriticalMutation<{ key: string; publication_status: PublicationStatus; reason?: string }>({
    call: (input) => updateFn({ data: input }),
    toastNamespace: `publication:${deliverableKey}`,
    onSuccess: async () => { await onDone(); },
    onConflict: async () => { onConflictDetected(); await onDone(); },
  });

  const triggerTransition = (t: Transition, reason?: string) => {
    if (mutation.isPending || mutation.isReconfirming) return;
    const isRepublish = currentStatus === "paused" && t.status === "published";
    if ((t.requiresReason || isRepublish) && !reason) {
      setReasonPrompt({ transition: t });
      return;
    }
    mutation.execute(
      { key: deliverableKey, publication_status: t.status, reason },
      expectedUpdatedAt,
    );
  };

  return (
    <div className="mt-4 flex flex-wrap gap-2 rounded-xl border border-black/[0.06] bg-black/[0.02] p-3">
      {TRANSITIONS.map((t) => (
        <button
          key={t.status}
          disabled={mutation.isPending || mutation.isReconfirming || currentStatus === t.status}
          className={`inline-flex items-center gap-1.5 rounded-lg border border-black/[0.08] bg-white px-3 py-1.5 text-xs font-semibold hover:bg-black/[0.03] disabled:opacity-40 ${t.tone}`}
          onClick={() => triggerTransition(t)}
        >
          <t.icon size={12} /> {t.label}
        </button>
      ))}
      <div className="ml-auto flex items-center gap-2">
        {mutation.isPending && <span className="text-xs text-[var(--gray-ink)]">Aplicando…</span>}
        <Link to="/membros/entregaveis/$key" params={{ key: deliverableKey }} target="_blank" className="inline-flex items-center gap-1.5 rounded-lg border border-black/[0.08] bg-white px-3 py-1.5 text-xs font-semibold hover:bg-black/[0.03]">
          <ExternalLink size={12} /> Ver como utilizador
        </Link>
      </div>

      {reasonPrompt && (
        <ReasonDialog
          open
          title={`Confirmar: ${reasonPrompt.transition.label}`}
          description={
            currentStatus === "paused" && reasonPrompt.transition.status === "published"
              ? "Republicar exige checklist completo, schema homologado e ausência de alertas críticos. Documente a razão."
              : "Registrada em auditoria. Descreva por que esta ação está sendo executada agora."
          }
          confirmLabel={reasonPrompt.transition.label}
          destructive={reasonPrompt.transition.status === "paused" || reasonPrompt.transition.status === "deprecated"}
          pending={mutation.isPending}
          onCancel={() => setReasonPrompt(null)}
          onConfirm={(reason) => {
            const t = reasonPrompt.transition;
            setReasonPrompt(null);
            triggerTransition(t, reason);
          }}
        />
      )}

      <ConflictDialog
        open={mutation.isReconfirming}
        entityLabel={entityLabel}
        currentState={mutation.conflictState}
        pending={mutation.isPending}
        onCancel={mutation.dismissConflict}
        onReconfirm={() => {
          const fresh = (mutation.conflictState as any)?.updated_at ?? null;
          mutation.reconfirm(fresh);
        }}
      />
    </div>
  );
}

// ============ TABS ============
function SummaryTab({ publication, entry, totalUsers }: { publication: any; entry: any; totalUsers: number }) {
  const pub = publication;
  return (
    <div className="mt-4 space-y-4 text-sm">
      <InfoGrid rows={[
        ["Estado", pub ? PUBLICATION_STATUS_META[pub.publicationStatus as PublicationStatus].label : "—"],
        ["Schema version", `v${pub?.schemaVersion ?? entry.schemaVersion ?? 1}`],
        ["Formatos permitidos", (pub?.allowedFormats ?? entry.formats ?? []).join(", ") || "—"],
        ["Regeneração", pub?.allowRegeneration ? "Permitida" : "Bloqueada"],
        ["Batch", pub?.allowBatch ? "Permitido" : "Bloqueado"],
        ["Publicado em", formatDateTime(pub?.publishedAt)],
        ["Pausado em", formatDateTime(pub?.pausedAt)],
        ["Descontinuado em", formatDateTime(pub?.deprecatedAt)],
        ["Utilizadores com este entregável", String(totalUsers)],
      ]} />
      {pub?.notes && (
        <div className="rounded-lg border border-black/[0.06] bg-black/[0.02] p-3">
          <div className="text-xs font-semibold uppercase text-[var(--gray-ink)]">Notas editoriais</div>
          <p className="mt-1 whitespace-pre-wrap text-sm text-[var(--navy)]">{pub.notes}</p>
        </div>
      )}
    </div>
  );
}

function InfoGrid({ rows }: { rows: [string, string][] }) {
  return (
    <div className="divide-y divide-black/[0.05] rounded-lg border border-black/[0.06]">
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-4 px-3 py-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">{k}</span>
          <span className="text-right text-sm text-[var(--navy)]">{v}</span>
        </div>
      ))}
    </div>
  );
}

function ChecklistTab({
  checklist, deliverableKey, entityLabel, onDone, onConflictDetected,
}: {
  checklist: Array<{ criterionKey: string; status: string; updatedAt: string; category: string }>;
  deliverableKey: string;
  entityLabel: string;
  onDone: () => void | Promise<void>;
  onConflictDetected: () => void;
}) {
  const setFn = useServerFn(setChecklistCriterion);
  const byId = new Map(checklist.map((r) => [r.criterionKey, r]));

  const mutation = useCriticalMutation<{
    deliverable_key: string; criterion_id: string; criterion_category: string;
    status: string; is_required: boolean;
  }>({
    call: (input) => setFn({ data: input as any }),
    toastNamespace: `checklist:${deliverableKey}`,
    onSuccess: async () => { await onDone(); },
    onConflict: async () => { onConflictDetected(); await onDone(); },
  });

  const grouped: Record<string, typeof HOMOLOGATION_CRITERIA> = {};
  for (const c of HOMOLOGATION_CRITERIA) (grouped[c.category] ||= []).push(c);

  return (
    <div className="mt-4 space-y-4">
      {mutation.isPending && (
        <div className="rounded-md border border-black/10 bg-black/[0.02] px-3 py-2 text-xs text-[var(--gray-ink)]">
          Gravando alteração no checklist…
        </div>
      )}
      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat} className="rounded-lg border border-black/[0.06]">
          <div className="border-b border-black/[0.06] bg-black/[0.02] px-3 py-2 text-xs font-bold uppercase tracking-wide text-[var(--navy)]">{cat}</div>
          <div className="divide-y divide-black/[0.05]">
            {items.map((c) => {
              const row = byId.get(c.id);
              const status = row?.status ?? "pending";
              const rowUpdatedAt = row?.updatedAt ?? null;
              return (
                <div key={c.id} className="flex items-start gap-3 px-3 py-3">
                  <StatusIcon status={status} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[var(--navy)]">{c.label}{c.required && <span className="ml-1 text-red-500">*</span>}</div>
                    <p className="text-xs text-[var(--gray-ink)]">{c.description}</p>
                    {row?.updatedAt && (
                      <p className="mt-1 text-[10px] uppercase tracking-wide text-[var(--gray-ink)]">
                        Revisto em {formatDateTime(row.updatedAt)}
                      </p>
                    )}
                  </div>
                  <Select
                    value={status}
                    disabled={mutation.isPending || mutation.isReconfirming}
                    onValueChange={(v) => mutation.execute(
                      { deliverable_key: deliverableKey, criterion_id: c.id, criterion_category: c.category, status: v, is_required: c.required },
                      rowUpdatedAt,
                    )}
                  >
                    <SelectTrigger className="h-8 w-[140px] bg-white text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pendente</SelectItem>
                      <SelectItem value="approved">Aprovado</SelectItem>
                      <SelectItem value="rejected">Rejeitado</SelectItem>
                      <SelectItem value="not_applicable">N/A</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <ConflictDialog
        open={mutation.isReconfirming}
        entityLabel={`${entityLabel} — checklist`}
        currentState={mutation.conflictState}
        pending={mutation.isPending}
        onCancel={mutation.dismissConflict}
        onReconfirm={() => {
          const fresh = (mutation.conflictState as any)?.updated_at ?? null;
          mutation.reconfirm(fresh);
        }}
      />
    </div>
  );
}

function StatusIcon({ status }: { status: string }) {
  if (status === "approved") return <CheckCircle2 size={16} className="mt-0.5 text-[var(--green)]" />;
  if (status === "rejected") return <XCircle size={16} className="mt-0.5 text-red-600" />;
  if (status === "not_applicable") return <Circle size={16} className="mt-0.5 text-[var(--gray-ink)]" />;
  return <Circle size={16} className="mt-0.5 text-[var(--gold)]" />;
}

function DependenciesTab({ entry }: { entry: any }) {
  return (
    <div className="mt-4 space-y-3 text-sm">
      <div className="rounded-lg border border-black/[0.06] p-4">
        <div className="flex items-center gap-2 text-xs font-bold uppercase text-[var(--navy)]"><GitBranch size={12} /> Árvore de dependências</div>
        <div className="mt-3 text-sm">
          <div className="font-semibold">Business Plan</div>
          <div className="ml-4 mt-1 space-y-1 border-l-2 border-black/10 pl-3">
            <div className="text-xs uppercase text-[var(--gray-ink)]">Blocos obrigatórios</div>
            {entry.requiredBlocks.map((b: string) => (
              <div key={b} className="text-sm text-[var(--navy)]">• {b} <span className="text-red-500">*</span></div>
            ))}
            <div className="mt-2 text-xs uppercase text-[var(--gray-ink)]">Blocos de origem</div>
            {entry.sourceBlocks.filter((b: string) => !entry.requiredBlocks.includes(b)).map((b: string) => (
              <div key={b} className="text-sm text-[var(--gray-ink)]">• {b}</div>
            ))}
            <div className="mt-2 flex items-center gap-2 text-xs uppercase text-[var(--gray-ink)]">↓ Deriva em</div>
            <div className="text-sm font-semibold text-[var(--navy)]">{entry.title}</div>
            <div className="ml-4 mt-1 space-y-1 text-xs text-[var(--gray-ink)]">
              {(entry.editorialSections ?? []).map((s: string) => <div key={s}>• {s}</div>)}
            </div>
          </div>
        </div>
      </div>
      <p className="text-xs text-[var(--gray-ink)]">Estrutura somente-leitura. Editar dependências requer alteração do registry canônico.</p>
    </div>
  );
}

function VersionsTab({ versions }: { versions: Array<{ version: number; inputHash: string; createdAt: string; reason: string | null }> }) {
  if (!versions?.length) {
    return <EmptyState icon={History} title="Sem versões" description="Nenhum utilizador gerou este entregável ainda." />;
  }
  return (
    <div className="mt-4">
      <table className="w-full text-sm">
        <thead className="border-b border-black/[0.06] text-xs uppercase text-[var(--gray-ink)]">
          <tr>
            <th className="px-2 py-2 text-left">Versão</th>
            <th className="px-2 py-2 text-left">Hash</th>
            <th className="px-2 py-2 text-left">Data</th>
            <th className="px-2 py-2 text-left">Motivo</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-black/[0.05]">
          {versions.map((v) => (
            <tr key={v.version}>
              <td className="px-2 py-2 font-semibold">v{v.version}</td>
              <td className="px-2 py-2 font-mono text-xs text-[var(--gray-ink)]">{String(v.inputHash).slice(0, 12)}…</td>
              <td className="px-2 py-2 text-xs">{formatDateTime(v.createdAt)}</td>
              <td className="px-2 py-2 text-xs text-[var(--gray-ink)]">{v.reason ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============ METRICS (Onda 2.1.c — server-aggregated + defensivo) ============
function MetricsTab({
  metrics, unclassified, alerts, totalUsersWithDeliverable,
}: {
  metrics: any;
  unclassified: { total: number; breakdown: Record<string, number>; firstAt: string | null; lastAt: string | null };
  alerts: number;
  totalUsersWithDeliverable: number;
}) {
  if (!metrics) {
    return (
      <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        <ShieldAlert size={14} className="mr-1 inline" /> Métricas indisponíveis por falha de contrato.
      </div>
    );
  }
  const reliableSince = metrics.metricsReliableSince ? formatDate(metrics.metricsReliableSince) : "—";
  const validTotal = metrics.validEvents ?? 0;
  const invalidTotal = metrics.invalidEvents ?? 0;
  const invalidPct = validTotal + invalidTotal > 0
    ? Math.round((invalidTotal / (validTotal + invalidTotal)) * 100)
    : null;

  return (
    <div className="mt-4 space-y-4">
      <div className="rounded-lg border border-black/[0.06] bg-black/[0.02] p-3 text-xs text-[var(--gray-ink)]">
        Janela: {formatDateTime(metrics.windowStart)} → {formatDateTime(metrics.windowEnd)} · Timezone: {APP_TIMEZONE}
        <br />
        <strong>Dados confiáveis desde {reliableSince}.</strong> Eventos anteriores não são considerados nos cálculos.
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <Metric label="Visualizações" value={metrics.views} icon={Eye} />
        <Metric label="Projetos únicos" value={metrics.uniqueProjects} icon={Users} />
        <Metric label="Gerações" value={metrics.generations} icon={Play} />
        <Metric label="Regenerações" value={metrics.regenerations} icon={RefreshCw} />
        <Metric label="Exportações" value={metrics.exports} icon={FileText} />
        <Metric label="Impressões" value={metrics.prints} icon={FileText} />
        <Metric label="Taxa de geração" value={formatRate(metrics.generationRate)} icon={TrendingUp} />
        <Metric label="Taxa de exportação" value={formatRate(metrics.exportRate)} icon={TrendingUp} />
        <Metric label="Taxa de regeneração" value={formatRate(metrics.regenerationRate)} icon={TrendingUp} />
        <Metric label="Abandono" value={formatRate(metrics.abandonRate)} icon={AlertTriangle} />
        <Metric label="Tempo até exportação (mediana)" value={formatSeconds(metrics.medianSecondsToExport)} icon={Clock} />
        <Metric label="Feedback útil" value={formatRate(metrics.usefulFeedbackRate)} icon={MessageSquare} />
        <Metric label="Taxa de erro" value={formatRate(metrics.errorRate)} icon={AlertTriangle} />
        <Metric label="Alertas abertos" value={alerts} icon={AlertTriangle} />
        <Metric label="Utilizadores com doc" value={totalUsersWithDeliverable} icon={Users} />
      </div>

      <div className="rounded-lg border border-black/[0.06] p-4">
        <div className="mb-2 flex items-center justify-between text-xs">
          <span className="font-bold uppercase text-[var(--navy)]">Eventos não classificados</span>
          {invalidPct !== null && (
            <span className="text-[var(--gray-ink)]">{invalidPct}% dos eventos SDE recebidos</span>
          )}
        </div>
        {unclassified.total === 0 ? (
          <p className="text-xs text-[var(--gray-ink)]">Nenhum evento inválido no período. ✔</p>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap gap-3 text-xs text-[var(--gray-ink)]">
              <span>{unclassified.total} eventos</span>
              <span>· Primeiro: {formatDateTime(unclassified.firstAt)}</span>
              <span>· Último: {formatDateTime(unclassified.lastAt)}</span>
            </div>
            <table className="w-full text-xs">
              <thead className="text-left text-[10px] uppercase text-[var(--gray-ink)]">
                <tr><th className="py-1">Motivo</th><th className="py-1 text-right">Quantidade</th></tr>
              </thead>
              <tbody>
                {Object.entries(unclassified.breakdown)
                  .filter(([, n]) => (n as number) > 0)
                  .sort((a, b) => (b[1] as number) - (a[1] as number))
                  .map(([k, n]) => (
                    <tr key={k} className="border-t border-black/[0.04]">
                      <td className="py-1 font-mono text-[var(--navy)]">{k}</td>
                      <td className="py-1 text-right tabular-nums">{n as number}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
            <p className="mt-2 text-[11px] text-[var(--gray-ink)]">Agregados apenas. Nenhum payload ou conteúdo privado é exibido.</p>
          </>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, icon: Icon }: { label: string; value: any; icon: any }) {
  return (
    <div className="rounded-lg border border-black/[0.06] bg-white p-3">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-[var(--gray-ink)]">
        <Icon size={12} /> {label}
      </div>
      <div className="mt-1 font-display text-xl font-extrabold text-[var(--navy)]">{value}</div>
    </div>
  );
}

function FeedbackTab({ feedback, usefulRate }: { feedback: any; usefulRate: number | null }) {
  const fb = feedback;
  const total = fb.total || 1;
  const bars = [
    { key: "very_useful", label: "Muito útil", tone: "bg-[var(--green)]" },
    { key: "useful", label: "Útil", tone: "bg-emerald-400" },
    { key: "low_utility", label: "Pouco útil", tone: "bg-amber-400" },
    { key: "not_useful", label: "Não útil", tone: "bg-red-500" },
  ];
  return (
    <div className="mt-4 space-y-4">
      <div className="rounded-lg border border-black/[0.06] p-4">
        <div className="flex items-center justify-between text-xs uppercase text-[var(--gray-ink)]">
          <span className="flex items-center gap-2"><MessageSquare size={12} /> {fb.total} respostas</span>
          <span>Feedback útil: <strong className="text-[var(--navy)]">{formatRate(usefulRate)}</strong></span>
          {fb.formatIssues > 0 && <StatusPill tone="red">{fb.formatIssues} problemas de formato</StatusPill>}
        </div>
        <div className="mt-3 space-y-2">
          {bars.map((b) => {
            const n = fb.usefulness[b.key] ?? 0;
            const pct = Math.round((n / total) * 100);
            return (
              <div key={b.key}>
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span>{b.label}</span>
                  <span className="text-[var(--gray-ink)]">{n} · {pct}%</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-black/[0.06]">
                  <div className={`h-full ${b.tone}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {fb.topReasons.length > 0 && (
        <div className="rounded-lg border border-black/[0.06] p-4">
          <div className="mb-2 text-xs font-bold uppercase text-[var(--navy)]">Principais motivos</div>
          <ul className="space-y-1 text-sm">
            {fb.topReasons.map(([reason, count]: [string, number]) => (
              <li key={reason} className="flex justify-between text-[var(--navy)]">
                <span>{reason}</span>
                <span className="text-[var(--gray-ink)]">{count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="text-xs text-[var(--gray-ink)]">Somente estatísticas agregadas. Conteúdo privado do utilizador nunca é exposto.</p>
    </div>
  );
}

// ============ ROLLOUT ============
function RolloutTab({
  publication, rolloutGroups, entityLabel, onDone, onConflictDetected,
}: {
  publication: any;
  rolloutGroups: Array<{ id: string; group: string; enabled: boolean; percentage: number; updatedAt: string; eligibleCount: number }>;
  entityLabel: string;
  onDone: () => void | Promise<void>;
  onConflictDetected: () => void;
}) {
  const updGroup = useServerFn(updateRolloutGroup);
  const updPub = useServerFn(updatePublication);
  const allowedGroups: string[] = publication?.allowedGroups ?? [];
  const pubUpdatedAt: string | null = publication?.updatedAt ?? null;

  const groupsMutation = useCriticalMutation<{ key: string; allowed_groups: string[] }>({
    call: (input) => updPub({ data: input as any }),
    toastNamespace: `rollout-groups:${publication?.deliverableKey ?? ""}`,
    onSuccess: async () => { await onDone(); },
    onConflict: async () => { onConflictDetected(); await onDone(); },
  });

  const toggleGroup = (g: string) => {
    if (!publication || groupsMutation.isPending || groupsMutation.isReconfirming) return;
    const next = allowedGroups.includes(g) ? allowedGroups.filter((x) => x !== g) : [...allowedGroups, g];
    groupsMutation.execute({ key: publication.deliverableKey, allowed_groups: next }, pubUpdatedAt);
  };

  return (
    <div className="mt-4 space-y-3">
      {groupsMutation.isPending && (
        <div className="rounded-md border border-black/10 bg-black/[0.02] px-3 py-2 text-xs text-[var(--gray-ink)]">
          Atualizando grupos permitidos…
        </div>
      )}

      {(Object.keys(ROLLOUT_GROUP_META) as RolloutGroupKey[]).map((gk) => {
        const meta = ROLLOUT_GROUP_META[gk];
        const group = rolloutGroups.find((g) => g.id === gk);
        const active = allowedGroups.includes(gk);

        return (
          <RolloutGroupCard
            key={gk}
            groupKey={gk}
            meta={meta}
            group={group}
            active={active}
            disabledToggle={groupsMutation.isPending || groupsMutation.isReconfirming || !publication}
            onToggle={() => toggleGroup(gk)}
            updGroupFn={(input) => updGroup({ data: input as any })}
            onDone={onDone}
            onConflictDetected={onConflictDetected}
            entityLabel={entityLabel}
          />
        );
      })}

      <ConflictDialog
        open={groupsMutation.isReconfirming}
        entityLabel={`${entityLabel} — grupos permitidos`}
        currentState={groupsMutation.conflictState}
        pending={groupsMutation.isPending}
        onCancel={groupsMutation.dismissConflict}
        onReconfirm={() => {
          const fresh = (groupsMutation.conflictState as any)?.updated_at ?? null;
          groupsMutation.reconfirm(fresh);
        }}
      />
    </div>
  );
}

function RolloutGroupCard({
  groupKey, meta, group, active, disabledToggle, onToggle,
  updGroupFn, onDone, onConflictDetected, entityLabel,
}: {
  groupKey: RolloutGroupKey;
  meta: { label: string; description: string };
  group: { percentage: number; updatedAt: string; eligibleCount: number } | undefined;
  active: boolean;
  disabledToggle: boolean;
  onToggle: () => void;
  updGroupFn: (input: any) => Promise<any>;
  onDone: () => void | Promise<void>;
  onConflictDetected: () => void;
  entityLabel: string;
}) {
  const currentPercentage: number = group?.percentage ?? 0;
  const groupUpdatedAt: string | null = group?.updatedAt ?? null;

  const [pendingReason, setPendingReason] = useState<{ percentage: number } | null>(null);
  const [lastPersistState, setLastPersistState] = useState<SliderPersistState>("idle");

  const mutation = useCriticalMutation<{ group_key: string; percentage: number; reason?: string }>({
    call: (input) => updGroupFn(input),
    toastNamespace: `rollout-pct:${groupKey}`,
    onSuccess: async () => { setLastPersistState("persisted"); await onDone(); },
    onError: () => setLastPersistState("error"),
    onConflict: async () => { setLastPersistState("error"); onConflictDetected(); await onDone(); },
  });

  const commit = (next: number, reason?: string) => {
    if (mutation.isPending || mutation.isReconfirming) return;
    if (next === currentPercentage) { setLastPersistState("persisted"); return; }
    const isReduction = next < currentPercentage;
    if (isReduction && !reason) {
      setPendingReason({ percentage: next });
      return;
    }
    setLastPersistState("saving");
    mutation.execute(
      { group_key: groupKey, percentage: next, reason },
      groupUpdatedAt,
    );
  };

  const persistState: SliderPersistState = mutation.isPending ? "saving" : lastPersistState;

  return (
    <div className={`rounded-lg border p-3 ${active ? "border-[var(--green)]/40 bg-[var(--green)]/[0.03]" : "border-black/[0.06] bg-white"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-[var(--navy)]">{meta.label}</span>
            {active && <StatusPill tone="green">Ativo</StatusPill>}
          </div>
          <p className="text-xs text-[var(--gray-ink)]">{meta.description}</p>
          <p className="mt-1 text-xs text-[var(--gray-ink)]">
            {group?.eligibleCount ?? 0} elegíveis diretos · {currentPercentage}% de rollout · atualizado {formatDate(groupUpdatedAt)}
          </p>
        </div>
        <button
          onClick={onToggle}
          disabled={disabledToggle}
          className={`h-8 rounded-lg border px-3 text-xs font-semibold ${active ? "border-red-200 bg-white text-red-600" : "border-black/[0.08] bg-white hover:bg-black/[0.03]"} disabled:opacity-50`}
        >
          {active ? "Remover" : "Ativar"}
        </button>
      </div>

      {group && (
        <AccessibleRolloutSlider
          label={`Rollout ${meta.label}`}
          value={currentPercentage}
          disabled={mutation.isReconfirming}
          persistState={persistState}
          onCommit={(next) => commit(next)}
        />
      )}

      {pendingReason && (
        <ReasonDialog
          open
          title="Reduzir rollout"
          description={`Reduzir de ${currentPercentage}% para ${pendingReason.percentage}% impacta usuários que já receberam o entregável. Documente o motivo.`}
          confirmLabel="Reduzir rollout"
          destructive
          pending={mutation.isPending}
          onCancel={() => { setPendingReason(null); setLastPersistState("idle"); }}
          onConfirm={(reason) => {
            const pct = pendingReason.percentage;
            setPendingReason(null);
            commit(pct, reason);
          }}
        />
      )}

      <ConflictDialog
        open={mutation.isReconfirming}
        entityLabel={`${entityLabel} — ${meta.label}`}
        currentState={mutation.conflictState}
        pending={mutation.isPending}
        onCancel={mutation.dismissConflict}
        onReconfirm={() => {
          const fresh = (mutation.conflictState as any)?.updated_at ?? null;
          mutation.reconfirm(fresh);
        }}
      />
    </div>
  );
}
